import type { FC } from 'react'
import React, { useState, useEffect } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
import type { FormikProps } from 'formik'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Typography, Button, TextField, Select } from '@homeviews/shared-ui'
import { maybePlaceholderImage } from '@homeviews/utils'
import type { MediaItem } from '@homeviews/models'
import { MEDIA_TYPE_OPTIONS } from '../../helpers/settings'

const useStyles = makeStyles(() =>
  createStyles({
    root: {},
    imageWrapper: {
      position: 'relative',
      '& img': {
        width: '100%',
      },
    },
    formWrapper: {
      marginTop: 10,
    },
    formFooter: {
      display: 'flex',
      marginTop: 20,
      marginBottom: 20,
    },
    footerButtonWrapper: {
      marginRight: 10,
    },
    deleteButtonWrapper: {
      marginRight: 'auto',
    },
    saveButtonWrapper: {
      marginRight: 0,
    },
    alertText: {
      display: 'flex',
      alignItems: 'flex-start',
      margin: '20px 0',
      '& img': {
        marginRight: 10,
        flexShrink: 0,
        width: 15,
        marginTop: 2,
      },
    },
  }),
)

interface IEditForm {
  name?: string
  alt_text?: string
  type?: string
}

const emptyFormState = {
  name: '',
  alt_text: '',
  type: '',
}

interface IProps extends MediaItem {
  className?: string
  cancel: () => void
  updateMedia: (any) => void
  isUpdatingMedia: boolean
  error?: any
  handleDeleteMedia: (mediaId: number) => void
}

const EditMediaForm: FC<IProps> = ({
  className,
  cancel,
  updateMedia,
  isUpdatingMedia,
  id,
  file_name,
  name,
  url,
  alt_text,
  type,
  handleDeleteMedia,
}) => {
  const classes = useStyles()
  const [initialFormState, setInitialFormState] =
    useState<IEditForm>(emptyFormState)

  useEffect(() => {
    setInitialFormState({
      name: name || '',
      alt_text: alt_text || '',
      type: type || 'default',
    })
  }, [alt_text, id, name, type])

  const onSubmit = (values: IEditForm) => {
    updateMedia(values)
  }

  return (
    <div className={clsx(classes.root, className)}>
      <div className={classes.imageWrapper}>
        <Typography variant="h1">Editing {name || file_name || ''}</Typography>
        <img src={maybePlaceholderImage(url)} alt={name || ''} />
        <Typography className={classes.alertText}>
          <img src="assets/icons/medium/alert-primary_v2.svg" alt="Info icon" />
          When naming your images please include the name of the development
          where possible and a brief indication of the content.
        </Typography>
        <Formik
          initialValues={initialFormState}
          enableReinitialize
          onSubmit={onSubmit}
          validationSchema={Yup.object().shape({
            name: Yup.string().required('Name is required'),
            alt_text: Yup.string().required('Alt text is required'),
            type: Yup.string().required('Type is required'),
          })}
        >
          {(props: FormikProps<IEditForm>) => {
            const {
              values,
              touched,
              errors,
              handleBlur,
              handleChange,
              setFieldValue,
            } = props

            const handleSelectChange =
              (type: string) =>
              (option: { value: string | number; label: 'string' }) => {
                setFieldValue(type, option?.value || '')
              }

            const handleDeleteClick = () => {
              handleDeleteMedia(id)
            }

            const handleCancelClick = () => {
              cancel()
            }

            return (
              <Form className={classes.formWrapper} noValidate>
                <TextField
                  value={values.name}
                  data-id="t-name"
                  id="name"
                  name="name"
                  label="Name"
                  error={errors.name && touched.name}
                  helperText={errors.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  required
                />
                <TextField
                  value={values.alt_text}
                  data-id="t-alt_text"
                  id="alt_text"
                  label="Alt text"
                  name="alt_text"
                  error={errors.alt_text && touched.alt_text}
                  helperText={errors.alt_text}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  fullWidth
                  required
                />
                <Select
                  label="Type"
                  value={values.type || 'default'}
                  options={MEDIA_TYPE_OPTIONS}
                  onChange={handleSelectChange('type')}
                  required
                />
                <div className={classes.formFooter}>
                  <div
                    className={clsx(
                      classes.footerButtonWrapper,
                      classes.deleteButtonWrapper,
                    )}
                  >
                    <Button
                      variant="outlined"
                      customColor="red"
                      onClick={handleDeleteClick}
                    >
                      Remove media
                    </Button>
                  </div>
                  <div className={classes.footerButtonWrapper}>
                    <Button
                      variant="outlined"
                      customVariant="outlinedPrimary"
                      fullWidth
                      onClick={handleCancelClick}
                    >
                      Close without changes
                    </Button>
                  </div>
                  <div
                    className={clsx(
                      classes.footerButtonWrapper,
                      classes.saveButtonWrapper,
                    )}
                  >
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      type="submit"
                      customColor="primary"
                      isLoading={isUpdatingMedia}
                      // disabled={!!Object.keys(errors).length}
                    >
                      Save changes
                    </Button>
                  </div>
                </div>
              </Form>
            )
          }}
        </Formik>
      </div>
    </div>
  )
}

export default EditMediaForm
